import { Box, SxProps, Theme } from "@mui/material";

const DEFAULT_COVER = "/assets/playlist-default.png";

interface PlaylistCoverProps {
    /** Track image URLs in playlist order. Empty/undefined renders the default cover. */
    images?: (string | null | undefined)[];
    size?: number | string | { xs?: number; sm?: number; md?: number; lg?: number };
    borderRadius?: number;
    sx?: SxProps<Theme>;
}

/**
 * 0 songs -> the default note art. 1-3 -> the first song's own cover, same as Spotify/
 * Apple would show for a playlist that thin. 4+ -> a 2x2 of the first four, mirroring how
 * Spotify auto-generates a cover once a playlist has enough tracks to tile.
 */
export default function PlaylistCover({ images, size = 48, borderRadius = 1, sx }: PlaylistCoverProps) {
    const covers = (images ?? []).filter((src): src is string => !!src);

    const boxSx: SxProps<Theme> = {
        width: size,
        height: size,
        borderRadius,
        flexShrink: 0,
        ...sx,
    };

    if (covers.length === 0) {
        return <Box component="img" src={DEFAULT_COVER} alt="" sx={{ ...boxSx, objectFit: "cover" }} />;
    }

    if (covers.length < 4) {
        return (
            <Box
                component="img"
                src={covers[0]}
                onError={e => { e.currentTarget.src = DEFAULT_COVER; }}
                alt=""
                sx={{ ...boxSx, objectFit: "cover" }}
            />
        );
    }

    return (
        <Box
            sx={{
                ...boxSx,
                overflow: "hidden",
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gridTemplateRows: "1fr 1fr",
            }}
        >
            {covers.slice(0, 4).map((src, i) => (
                <Box
                    key={i}
                    component="img"
                    src={src}
                    onError={e => { e.currentTarget.src = DEFAULT_COVER; }}
                    alt=""
                    sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                />
            ))}
        </Box>
    );
}
